import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ChevronLeft, ChevronRight, Check, Upload, AlertCircle, School, Users, Settings, Palette, Globe } from 'lucide-react';

const TenantOnboardingWizard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default form data
  const defaultFormData = {
    // School Contact Information
    schoolName: '',
    subdomain: '',
    primaryAdminEmail: '',
    allowedEmailDomains: [''],
    schoolPhone: '',
    schoolAddress: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    contactPersonName: '',
    contactPersonTitle: '',
    contactPersonPhone: '',
    contactPersonEmail: '',
    
    // Organizational Structure
    schoolType: '',
    studentEnrollment: '',
    facultyStaffCount: '',
    calendarType: '',
    
    // Initial Configuration
    timeZone: '',
    defaultLanguage: 'en',
    academicYearStart: '',
    academicYearEnd: '',
    gradingPeriods: '',
    
    // Branding
    schoolLogo: null,
    primaryColor: '#1e40af',
    secondaryColor: '#3b82f6',
    schoolMascot: '',
    
    // Integrations
    googleWorkspace: false,
    microsoft365: false,
    sisSystem: '',
    otherIntegrations: '',
    
    // User Groups
    departments: [''],
    customGroups: ['']
  };

  // Initialize with prefilled data if coming from inquiry
  const [formData, setFormData] = useState({
    ...defaultFormData,
    ...location.state?.prefillData
  });

  const steps = [
    { id: 1, title: 'School Information', icon: School },
    { id: 2, title: 'Organization', icon: Users },
    { id: 3, title: 'Configuration', icon: Settings },
    { id: 4, title: 'Branding', icon: Palette },
    { id: 5, title: 'Integrations', icon: Globe },
    { id: 6, title: 'Review & Submit', icon: Check }
  ];

  const schoolTypes = [
    'Early Childhood',
    'Elementary School',
    'Middle School', 
    'High School',
    'K-5',
    'K-8',
    'K-12 School',
    'Charter School',
    'Private School',
    'Magnet School',
    'Other'
  ];

  // EXPANDED TIMEZONE LIST WITH INTERNATIONAL OPTIONS
  const timeZones = [
    { value: 'America/New_York', label: 'Eastern Time (US)' },
    { value: 'America/Chicago', label: 'Central Time (US)' },
    { value: 'America/Denver', label: 'Mountain Time (US)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
    { value: 'America/Anchorage', label: 'Alaska Time (US)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (US)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    { value: 'Europe/Rome', label: 'Rome (CET)' },
    { value: 'Europe/Madrid', label: 'Madrid (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)' },
    { value: 'Asia/Shanghai', label: 'Beijing/Shanghai (CST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
    { value: 'America/Toronto', label: 'Toronto (EST)' },
    { value: 'America/Vancouver', label: 'Vancouver (PST)' }
  ];

  const gradingPeriodOptions = [
    'Quarters (4 periods)',
    'Trimesters (3 periods)', 
    'Semesters (2 periods)',
    'Custom'
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const generateSubdomain = (schoolName) => {
    return schoolName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 30);
  };

  const handleSchoolNameChange = (value) => {
    updateFormData('schoolName', value);
    if (!formData.subdomain || formData.subdomain === generateSubdomain(formData.schoolName)) {
      updateFormData('subdomain', generateSubdomain(value));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.schoolName && formData.subdomain && formData.primaryAdminEmail && 
               formData.allowedEmailDomains[0] && formData.contactPersonName;
      case 2:
        return formData.schoolType && formData.studentEnrollment && formData.facultyStaffCount;
      case 3:
        return formData.timeZone && formData.academicYearStart && formData.academicYearEnd;
      case 4:
        return true; // Branding is optional
      case 5:
        return true; // Integrations are optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Import from your firebase config
      const { app } = await import('../../firebase/config');
      const functions = getFunctions(app, 'us-east1'); // Specify region
      const submitRequest = httpsCallable(functions, 'submitTenantRequest');
      
      // Prepare data for submission
      const submissionData = {
        ...formData,
        // Filter out empty array items
        allowedEmailDomains: formData.allowedEmailDomains.filter(domain => domain.trim()),
        departments: formData.departments.filter(dept => dept.trim()),
        customGroups: formData.customGroups.filter(group => group.trim()),
        createdAt: new Date().toISOString(),
        status: 'pending'
      };
      
      console.log('Submitting data:', submissionData);
      
      const result = await submitRequest(submissionData);
      
      if (result.data.success) {
        alert('Thank you! Your school onboarding request has been submitted successfully. You will receive an email confirmation shortly, and we will review your request within 24-48 hours.');
        navigate('/super-admin');
      } else {
        alert(`Error submitting request: ${result.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      alert(`Error submitting request: ${error.message || 'Please check the console for details.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">School Contact Information</h3>
              <p className="text-gray-600"><i>**Do not refresh until submitted, results will be lost</i></p>
              <p className="text-gray-600">Tell us about your school and primary contact</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    School Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schoolName}
                    onChange={(e) => handleSchoolNameChange(e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your school name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Subdomain <span className="text-red-500">*</span>
                  </label>
                  <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
                    <input
                      type="text"
                      value={formData.subdomain}
                      onChange={(e) => updateFormData('subdomain', e.target.value)}
                      className="flex-1 px-4 py-3 text-base border-0 rounded-l-lg focus:outline-none"
                      placeholder="subdomain"
                      required
                    />
                    <span className="px-4 py-3 bg-gray-50 text-sm text-gray-600 rounded-r-lg border-l border-gray-200">
                      .reliefroster.com
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Primary Administrator Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.primaryAdminEmail}
                    onChange={(e) => updateFormData('primaryAdminEmail', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="admin@yourschool.org"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    School Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.schoolPhone}
                    onChange={(e) => updateFormData('schoolPhone', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Allowed Email Domains <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    {formData.allowedEmailDomains.map((domain, index) => (
                      <div key={index} className="flex gap-3">
                        <input
                          type="text"
                          value={domain}
                          onChange={(e) => updateArrayItem('allowedEmailDomains', index, e.target.value)}
                          className="flex-1 px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="yourschool.org"
                          required={index === 0}
                        />
                        {formData.allowedEmailDomains.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeArrayItem('allowedEmailDomains', index)}
                            className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg border border-red-300 transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayItem('allowedEmailDomains')}
                      className="text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg text-sm border border-blue-300 transition-colors"
                    >
                      + Add another domain
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    School Address
                  </label>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={formData.streetAddress}
                      onChange={(e) => updateFormData('streetAddress', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Street Address"
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => updateFormData('city', e.target.value)}
                        className="px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => updateFormData('state', e.target.value)}
                        className="px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="State"
                      />
                      <input
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => updateFormData('zipCode', e.target.value)}
                        className="px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="ZIP Code"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-6">Primary Contact Person</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.contactPersonName}
                      onChange={(e) => updateFormData('contactPersonName', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contactPersonPhone}
                      onChange={(e) => updateFormData('contactPersonPhone', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Title/Position
                    </label>
                    <input
                      type="text"
                      value={formData.contactPersonTitle}
                      onChange={(e) => updateFormData('contactPersonTitle', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Principal, IT Director, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={formData.contactPersonEmail}
                      onChange={(e) => updateFormData('contactPersonEmail', e.target.value)}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="contact@yourschool.org"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Organizational Structure</h3>
              <p className="text-gray-600">Help us understand your school's organization</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    School Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.schoolType}
                    onChange={(e) => updateFormData('schoolType', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    required
                  >
                    <option value="">Select school type</option>
                    {schoolTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Student Enrollment <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.studentEnrollment}
                    onChange={(e) => updateFormData('studentEnrollment', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="500"
                    required
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Calendar Type
                  </label>
                  <select
                    value={formData.calendarType}
                    onChange={(e) => updateFormData('calendarType', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                  >
                    <option value="">Select calendar type</option>
                    <option value="traditional">Traditional (Aug-June)</option>
                    <option value="year-round">Year-Round</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Faculty/Staff Count <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.facultyStaffCount}
                    onChange={(e) => updateFormData('facultyStaffCount', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="50"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Departments/Groups
              </label>
              <div className="space-y-3">
                {formData.departments.map((dept, index) => (
                  <div key={index} className="flex gap-3">
                    <input
                      type="text"
                      value={dept}
                      onChange={(e) => updateArrayItem('departments', index, e.target.value)}
                      className="flex-1 px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Math, Science, Administration, etc."
                    />
                    {formData.departments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('departments', index)}
                        className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg border border-red-300 transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('departments')}
                  className="text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg text-sm border border-blue-300 transition-colors"
                >
                  + Add department
                </button>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Initial Configuration</h3>
              <p className="text-gray-600">Set up your basic system preferences</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Time Zone <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.timeZone}
                    onChange={(e) => updateFormData('timeZone', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    required
                  >
                    <option value="">Select time zone</option>
                    <optgroup label="United States">
                      <option value="America/New_York">Eastern Time (US)</option>
                      <option value="America/Chicago">Central Time (US)</option>
                      <option value="America/Denver">Mountain Time (US)</option>
                      <option value="America/Los_Angeles">Pacific Time (US)</option>
                      <option value="America/Anchorage">Alaska Time (US)</option>
                      <option value="Pacific/Honolulu">Hawaii Time (US)</option>
                    </optgroup>
                    <optgroup label="Canada">
                      <option value="America/Toronto">Toronto (EST)</option>
                      <option value="America/Vancouver">Vancouver (PST)</option>
                    </optgroup>
                    <optgroup label="Europe">
                      <option value="Europe/London">London (GMT)</option>
                      <option value="Europe/Paris">Paris (CET)</option>
                      <option value="Europe/Berlin">Berlin (CET)</option>
                      <option value="Europe/Rome">Rome (CET)</option>
                      <option value="Europe/Madrid">Madrid (CET)</option>
                    </optgroup>
                    <optgroup label="Asia">
                      <option value="Asia/Tokyo">Tokyo (JST)</option>
                      <option value="Asia/Seoul">Seoul (KST)</option>
                      <option value="Asia/Shanghai">Beijing/Shanghai (CST)</option>
                      <option value="Asia/Singapore">Singapore (SGT)</option>
                      <option value="Asia/Kolkata">India (IST)</option>
                      <option value="Asia/Dubai">Dubai (GST)</option>
                    </optgroup>
                    <optgroup label="Australia & New Zealand">
                      <option value="Australia/Sydney">Sydney (AEST)</option>
                      <option value="Australia/Melbourne">Melbourne (AEST)</option>
                      <option value="Australia/Perth">Perth (AWST)</option>
                      <option value="Pacific/Auckland">Auckland (NZST)</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Academic Year Start <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.academicYearStart}
                    onChange={(e) => updateFormData('academicYearStart', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Default Language
                  </label>
                  <select
                    value={formData.defaultLanguage}
                    onChange={(e) => updateFormData('defaultLanguage', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Academic Year End <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.academicYearEnd}
                    onChange={(e) => updateFormData('academicYearEnd', e.target.value)}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Grading Periods
              </label>
              <select
                value={formData.gradingPeriods}
                onChange={(e) => updateFormData('gradingPeriods', e.target.value)}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
              >
                <option value="">Select grading period structure</option>
                {gradingPeriodOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Branding & Customization</h3>
              <p className="text-gray-600">Customize the look and feel of your Relief Roster</p>
            </div>
            
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  School Logo
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600 mb-4">Upload your school logo (PNG, JPG, or SVG)</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => updateFormData('schoolLogo', e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Primary Brand Color
                  </label>
                  <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => updateFormData('primaryColor', e.target.value)}
                      className="w-16 h-12 border-0 rounded-l-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.primaryColor}
                      onChange={(e) => updateFormData('primaryColor', e.target.value)}
                      className="flex-1 px-4 py-3 text-base border-0 rounded-r-lg focus:outline-none"
                      placeholder="#1e40af"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Secondary Brand Color
                  </label>
                  <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
                    <input
                      type="color"
                      value={formData.secondaryColor}
                      onChange={(e) => updateFormData('secondaryColor', e.target.value)}
                      className="w-16 h-12 border-0 rounded-l-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.secondaryColor}
                      onChange={(e) => updateFormData('secondaryColor', e.target.value)}
                      className="flex-1 px-4 py-3 text-base border-0 rounded-r-lg focus:outline-none"
                      placeholder="#3b82f6"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  School Mascot/Team Name
                </label>
                <input
                  type="text"
                  value={formData.schoolMascot}
                  onChange={(e) => updateFormData('schoolMascot', e.target.value)}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Eagles, Lions, Warriors, etc."
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">System Integrations</h3>
              <p className="text-gray-600">Connect Relief Roster with your existing systems</p>
            </div>
            
            <div className="space-y-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Productivity Integrations</h4>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="googleWorkspace"
                      checked={formData.googleWorkspace}
                      onChange={(e) => updateFormData('googleWorkspace', e.target.checked)}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="googleWorkspace" className="ml-3 text-base text-gray-900">
                      Google Workspace Integration
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="microsoft365"
                      checked={formData.microsoft365}
                      onChange={(e) => updateFormData('microsoft365', e.target.checked)}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="microsoft365" className="ml-3 text-base text-gray-900">
                      Microsoft 365 Integration
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Student Information System (SIS)
                </label>
                <select
                  value={formData.sisSystem}
                  onChange={(e) => updateFormData('sisSystem', e.target.value)}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                >
                  <option value="">Select SIS (if applicable)</option>
                  <option value="powerschool">PowerSchool</option>
                  <option value="infinite-campus">Infinite Campus</option>
                  <option value="skyward">Skyward</option>
                  <option value="studentplus">Student Plus</option>
                  <option value="other">Other</option>
                  <option value="Veracross">Veracross</option>
                  <option value="FACTS">FACTS</option>
                  <option value="Admin PLUS">Admin PLUS</option>
                  <option value="Big SIS">Big SIS</option>
                  <option value="iSAMS">iSAMS</option>
                  <option value="none">No SIS</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Other Integrations or Requirements
                </label>
                <textarea
                  value={formData.otherIntegrations}
                  onChange={(e) => updateFormData('otherIntegrations', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  placeholder="Describe any other systems you need integrated or special requirements..."
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Review & Submit</h3>
              <p className="text-gray-600">Please review your information before submitting</p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex">
                <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5" />
                <div className="ml-4">
                  <h4 className="text-base font-semibold text-blue-800">What happens next?</h4>
                  <p className="text-sm text-blue-700 mt-2">
                    After submission, our team will review your information and contact you within 24-48 hours. 
                    Once approved, we'll set up your Relief Roster tenant and send you login instructions.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">School Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div><strong>School:</strong> {formData.schoolName}</div>
                  <div><strong>Subdomain:</strong> {formData.subdomain}.reliefroster.com</div>
                  <div><strong>Admin Email:</strong> {formData.primaryAdminEmail}</div>
                  <div><strong>Contact:</strong> {formData.contactPersonName}</div>
                  <div className="md:col-span-2">
                    <strong>Allowed Domains:</strong> {formData.allowedEmailDomains.filter(d => d.trim()).join(', ')}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Organization</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div><strong>Type:</strong> {formData.schoolType}</div>
                  <div><strong>Calendar:</strong> {formData.calendarType}</div>
                  <div><strong>Enrollment:</strong> {formData.studentEnrollment} students</div>
                  <div><strong>Staff:</strong> {formData.facultyStaffCount} faculty/staff</div>
                  {formData.departments.filter(d => d.trim()).length > 0 && (
                    <div className="md:col-span-2">
                      <strong>Departments:</strong> {formData.departments.filter(d => d.trim()).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div><strong>Time Zone:</strong> {timeZones.find(tz => tz.value === formData.timeZone)?.label}</div>
                  <div><strong>Language:</strong> {formData.defaultLanguage === 'en' ? 'English' : formData.defaultLanguage === 'es' ? 'Spanish' : 'French'}</div>
                  <div className="md:col-span-2">
                    <strong>Academic Year:</strong> {formData.academicYearStart} to {formData.academicYearEnd}
                  </div>
                  {formData.gradingPeriods && (
                    <div className="md:col-span-2">
                      <strong>Grading Periods:</strong> {formData.gradingPeriods}
                    </div>
                  )}
                </div>
              </div>

              {(formData.schoolMascot || formData.primaryColor !== '#1e40af' || formData.secondaryColor !== '#3b82f6') && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Branding</h4>
                  <div className="space-y-3 text-sm text-gray-700">
                    {formData.schoolMascot && (
                      <div><strong>Mascot:</strong> {formData.schoolMascot}</div>
                    )}
                    <div className="flex items-center gap-6">
                      <div className="flex items-center">
                        <div 
                          className="w-8 h-8 rounded border border-gray-300 mr-3" 
                          style={{ backgroundColor: formData.primaryColor }}
                        ></div>
                        <span><strong>Primary:</strong> {formData.primaryColor}</span>
                      </div>
                      <div className="flex items-center">
                        <div 
                          className="w-8 h-8 rounded border border-gray-300 mr-3" 
                          style={{ backgroundColor: formData.secondaryColor }}
                        ></div>
                        <span><strong>Secondary:</strong> {formData.secondaryColor}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(formData.googleWorkspace || formData.microsoft365 || formData.sisSystem || formData.otherIntegrations) && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Integrations</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    {formData.googleWorkspace && <div>• Google Workspace Integration</div>}
                    {formData.microsoft365 && <div>• Microsoft 365 Integration</div>}
                    {formData.sisSystem && <div><strong>SIS:</strong> {formData.sisSystem}</div>}
                    {formData.otherIntegrations && (
                      <div><strong>Other Requirements:</strong> {formData.otherIntegrations}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ marginLeft: '260px', padding: '40px', flex: 1, backgroundColor: '#FFFFFF', minHeight: '100vh', boxSizing: 'border-box' }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">School Onboarding Request</h1>
          <p className="text-lg text-gray-600">Provide your school's information to get started with Relief Roster</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-200 ${
                    isCompleted 
                      ? 'bg-green-500 border-green-500 text-white shadow-lg' 
                      : isActive 
                        ? 'bg-blue-500 border-blue-500 text-white shadow-lg' 
                        : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    {isCompleted ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <Icon className="w-6 h-6" />
                    )}
                  </div>
                  <span className={`ml-3 text-sm font-medium hidden md:block transition-colors duration-200 ${
                    isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                  {step.id < steps.length && (
                    <div className={`hidden md:block w-16 h-1 ml-6 transition-colors duration-200 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-10" style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
          {renderStep()}
          
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-600 text-white hover:bg-gray-700 shadow-lg hover:shadow-xl transform hover:scale-105'
              }`}
            >
              <ChevronLeft className="w-6 h-6 mr-2" />
              Previous
            </button>

            {currentStep < 6 ? (
              <button
                onClick={nextStep}
                disabled={!validateStep(currentStep)}
                className={`flex items-center px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                  validateStep(currentStep)
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:scale-105'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
                <ChevronRight className="w-6 h-6 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !validateStep(currentStep)}
                className={`px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                  isSubmitting || !validateStep(currentStep)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl transform hover:scale-105'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3 inline-block"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantOnboardingWizard;